import axios from 'axios';
import Config from '../config/index';
import { firebaseAppQP } from './firebaseQP';
import store from '../store';
import { clearAuthState } from '../slices/auth/authSlice';

const LOGIN_ROUTE = '/account/login';
const isOnLoginRoute = () => window.location.pathname.startsWith(LOGIN_ROUTE);

let refreshingPromise: Promise<string> | null = null;

/**
 * BE v2 `authQP.middleware`: sin token → 404 "QP Token not found"; verifyIdToken falla (vencido/inválido) → 401 "Ivalid QP Token".
 * Hasta que Firebase restaure la sesión, `currentUser` puede ser null y el BE responde ese 404.
 */
let authStateInitialized = false;
let authInitPromise: Promise<void> | null = null;

function ensureFirebaseAuthInitialized(): Promise<void> {
  if (authStateInitialized) return Promise.resolve();
  if (!authInitPromise) {
    authInitPromise = new Promise((resolve) => {
      const unsub = firebaseAppQP.auth().onAuthStateChanged(() => {
        unsub();
        authStateInitialized = true;
        resolve();
      });
    });
  }
  return authInitPromise;
}

function getApiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && 'message' in data) {
    const msg = (data as { message?: unknown }).message;
    return typeof msg === 'string' ? msg : '';
  }
  return '';
}

const V2_MSG_QP_TOKEN_NOT_FOUND = 'qp token not found';
/** Typo real en authQP.middleware: `Ivalid QP Token` */
const V2_MSG_IVALID_QP_TOKEN = 'ivalid qp token';

function normalizeApiMessage(error: unknown): string {
  return getApiErrorMessage(error).trim().toLowerCase();
}

/** Solo el 404 que emite `authQP.middleware` (no otros 404 de negocio). */
function isV2AuthQPMissingToken404(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 404) return false;
  return normalizeApiMessage(error) === V2_MSG_QP_TOKEN_NOT_FOUND;
}

/**
 * Solo el 401 de `authQP.middleware` cuando falla `verifyIdToken` (token vencido o inválido).
 * Otros 401 en v2 (p. ej. "Authorization token is required", API key de cron) no se interceptan aquí.
 */
function isV2AuthQPInvalidToken401(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 401) return false;
  const msg = normalizeApiMessage(error);
  return msg === V2_MSG_IVALID_QP_TOKEN || msg === 'invalid qp token';
}

type ApiConfig = {
  url: string;
  timeout: number;
};

export const DEFAULT_API_CONFIG: ApiConfig = {
  url: Config.API_URL,
  timeout: 60000
};

const axiosInst = axios.create({
  baseURL: DEFAULT_API_CONFIG.url,
  timeout: DEFAULT_API_CONFIG.timeout
});

axiosInst.interceptors.request.use(async (config) => {
  await ensureFirebaseAuthInitialized();
  const user = firebaseAppQP.auth().currentUser;
  if (user) {
    config.headers = config.headers || {};
    const token = await user.getIdToken();

    config.headers['authorizationqp'] = `Bearer ${token}`;
    
  }
  return config;
});

axiosInst.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as any;
    const url: string = originalRequest?.url || '';

    // Evita bucles: no interceptar endpoints de autenticación
    const isAuthRoute = (() => {
      try {
        const u = new URL(url || '', DEFAULT_API_CONFIG.url);
        return (
          u.pathname.startsWith('/auth/') ||
          u.hostname === 'securetoken.googleapis.com'
        );
      } catch {
        return false;
      }
    })();

    const shouldRetryAuth =
      (isV2AuthQPInvalidToken401(error) || isV2AuthQPMissingToken404(error)) &&
      !isAuthRoute &&
      !originalRequest._retry;

    if (shouldRetryAuth) {
      originalRequest._retry = true;

      try {
        await ensureFirebaseAuthInitialized();
        const user = firebaseAppQP.auth().currentUser;
        if (!user) {
          store.dispatch(clearAuthState());
          try {
            await firebaseAppQP.auth().signOut();
          } catch {}
          if (!isOnLoginRoute()) {
            window.location.assign(LOGIN_ROUTE);
          }
          throw error;
        }

        // Un único refresh compartido para todas las 401/404-auth concurrentes
        if (!refreshingPromise) {
          refreshingPromise = user.getIdToken(true).finally(() => {
            refreshingPromise = null;
          });
        }

        const newToken = await refreshingPromise;

        // Actualiza credenciales y reintenta la solicitud original
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['authorizationqp'] = `Bearer ${newToken}`;

        return await axiosInst(originalRequest);
      } catch (e) {
        // Refresh no disponible o fallido: cerrar sesión y redirigir
        store.dispatch(clearAuthState());
        try {
          await firebaseAppQP.auth().signOut();
        } catch {}
        if (!isOnLoginRoute()) {
          window.location.assign(LOGIN_ROUTE);
        }
        throw e;
      }
    }

    // Cualquier otro error, sigue su curso
    throw error;
  }
);

export default axiosInst;
